import { FC, useCallback, useContext, useEffect } from 'react';
import { RadioButton, RadioButtonGroup } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { useStore } from '@tanstack/react-store';

import { useWizardStep } from 'components/full-page-wizard';

import { AfeWizardCtx } from '../context';
import { afeEnumsLoadableAtom } from '../atoms';
import type { EnumOption, WellType } from '../types';
import { AfeWellMap } from '../components/afe-well-map';
import { ExistingWellForm } from './existing-well-form';
import { ImportedWellForm } from './imported-well-form';
import styles from './well-information-step.module.scss';

/**
 * Step 3 — Well Information. Renders a Well Type radio to choose between
 * Existing Well and Imported Well, then conditionally renders the appropriate
 * form. Manages the validation bridge (Next button) and cross-field
 * dependencies (basin → formation, state → county).
 * @return {JSX.Element} The well information step content
 */
const WellInformationStep: FC = () => {
  const { form } = useContext(AfeWizardCtx)!;
  const { setNextEnabled } = useWizardStep();
  const enumsLoadable = useAtomValue(afeEnumsLoadableAtom);

  const wellTypeOptions: EnumOption[] =
    enumsLoadable.state === 'hasData' ? enumsLoadable.data.wellType : [];

  // Scalar selectors for validation bridge
  const wellType = useStore(form.store, (s) => s.values.wellType);
  const afeType = useStore(form.store, (s) => s.values.afeType);
  const uwi = useStore(form.store, (s) => s.values.uwi);
  const importedWellName = useStore(
    form.store,
    (s) => s.values.importedWellName
  );
  const plannedLateralLength = useStore(
    form.store,
    (s) => s.values.plannedLateralLength
  );
  const plannedTvd = useStore(form.store, (s) => s.values.plannedTvd);
  const showDcAndE = afeType === 'dc_and_e';

  // Validation bridge
  useEffect(() => {
    if (!wellType) {
      setNextEnabled(false);
      return;
    }
    const dncValid =
      !showDcAndE || (plannedLateralLength !== null && plannedTvd !== null);
    if (wellType === 'existing_well') {
      setNextEnabled(!!uwi && dncValid);
    } else {
      setNextEnabled(!!importedWellName && dncValid);
    }
  }, [
    wellType,
    uwi,
    importedWellName,
    plannedLateralLength,
    plannedTvd,
    showDcAndE,
    setNextEnabled,
  ]);

  /**
   * Resets all well-related form fields when switching well type.
   * @param {WellType} newWellType - The newly selected well type
   */
  const handleWellTypeChange = useCallback(
    (newWellType: WellType) => {
      form.setFieldValue('wellType', newWellType);
      // Existing Well fields
      form.setFieldValue('uwi', null);
      form.setFieldValue('slotId', null);
      // Imported Well fields
      form.setFieldValue('importedWellName', null);
      form.setFieldValue('stateId', null);
      form.setFieldValue('countyId', null);
      form.setFieldValue('operator', null);
      // Shared fields
      form.setFieldValue('basinName', null);
      form.setFieldValue('reportedFormation', null);
      form.setFieldValue('plannedLateralLength', null);
      form.setFieldValue('plannedTvd', null);
    },
    [form]
  );

  // Cross-field handlers
  const handleBasinChange = useCallback(
    (basinName: string | null) => {
      form.setFieldValue('basinName', basinName);
      form.setFieldValue('reportedFormation', null);
    },
    [form]
  );

  const handleStateChange = useCallback(
    (stateId: number | null) => {
      form.setFieldValue('stateId', stateId);
      form.setFieldValue('countyId', null);
    },
    [form]
  );

  return (
    <div className={styles.splitLayout}>
      <div className={styles.formPanel}>
        <h2 className={styles.heading}>Well Information*</h2>
        <p className={styles.subtitle}>
          Select well type and provide well details
        </p>

        <form.Field name="wellType">
          {(field) => (
            <RadioButtonGroup
              name="wellType"
              legendText="Well Type *"
              valueSelected={field.state.value ?? ''}
              onChange={(value: string | number | undefined) =>
                handleWellTypeChange(value as WellType)
              }
              orientation="horizontal"
              className={styles.radioGroup}
            >
              {wellTypeOptions.map((opt) => (
                <RadioButton
                  key={opt.value}
                  value={opt.value}
                  labelText={opt.label}
                />
              ))}
            </RadioButtonGroup>
          )}
        </form.Field>

        {wellType === 'existing_well' && (
          <ExistingWellForm
            onBasinChange={handleBasinChange}
            showDcAndE={showDcAndE}
          />
        )}
        {wellType === 'generic_well' && (
          <ImportedWellForm
            onBasinChange={handleBasinChange}
            onStateChange={handleStateChange}
            showDcAndE={showDcAndE}
          />
        )}
      </div>
      <div className={styles.mapPanel}>
        <AfeWellMap uwi={uwi} />
      </div>
    </div>
  );
};

export { WellInformationStep };
